import { useEffect, useState } from 'react'
import { baseApiClient } from '@/lib/api/client'

interface BannerData {
  show: boolean
  label: string
  bg_color: string
  text_color: string
  api_host: string
}

async function fetchConsoleBanner(): Promise<BannerData | null> {
  try {
    const res = await baseApiClient.get<{ banner: BannerData }>('/v1/config/console')
    return res.data.banner ?? null
  } catch (err) {
    console.warn('[EnvironmentBanner] Failed to load console config:', err)
    return null
  }
}

export function EnvironmentBanner() {
  const [config, setConfig] = useState<BannerData | null>(null)

  useEffect(() => {
    fetchConsoleBanner().then(setConfig)
  }, [])

  if (!config || !config.show) return null

  // Derive stripe color from bg (slightly darker overlay)
  const stripe = 'rgba(0,0,0,0.10)'

  return (
    <div
      role="banner"
      aria-label={`Environment: ${config.label}`}
      style={{
        backgroundColor: config.bg_color,
        backgroundImage: `repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 8px,
          ${stripe} 8px,
          ${stripe} 16px
        )`,
        color: config.text_color,
        height: '28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        flexShrink: 0,
        zIndex: 50,
        fontFamily: '"SF Mono", "Fira Code", ui-monospace, monospace',
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.09em',
        userSelect: 'none',
      }}
    >
      {/* Pulsing dot */}
      <span
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          backgroundColor: config.text_color,
          opacity: 0.8,
          flexShrink: 0,
          animation: 'env-dot-pulse 2.4s ease-in-out infinite',
        }}
      />

      {/* Env label (pre-built on server: "CN · STAGING", "GLOBAL · PRODUCTION", etc.) */}
      <span style={{ textTransform: 'uppercase' }}>{config.label}</span>

      {/* API host */}
      <span style={{ opacity: 0.45 }}>·</span>
      <span style={{ opacity: 0.65, fontWeight: 400 }}>{config.api_host}</span>

      <style>{`
        @keyframes env-dot-pulse {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 0.25; }
        }
      `}</style>
    </div>
  )
}
